import React, { useState } from "react";
import { Box, Heading, Stack, Input, Textarea, Button, Text, useToast, useColorModeValue } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { getToken } from "utils/auth";
import config from "config";

const CreatePost = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const history = useHistory();
  const toast = useToast();  // For showing feedback notifications

  // Max lengths
  const maxTitleLength = 70;
  const maxContentLength = 500;

  // Colors for light/dark mode
  const bgColor = useColorModeValue("gray.50", "gray.800");
  const boxBgColor = useColorModeValue("white", "gray.700");
  const inputBorderColor = useColorModeValue("gray.300", "gray.600");
  const hoverBorderColor = useColorModeValue("teal.400", "teal.300");
  const focusBorderColor = useColorModeValue("teal.500", "teal.400");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const headingColor = useColorModeValue("teal.600", "teal.300");
  const buttonHoverColor = useColorModeValue("teal.600", "teal.500");

  const handleCreatePost = async () => {
    if (title.length === 0 || content.length === 0) {
      toast({
        title: "All fields are required.",
        description: "Please provide both a title and content for the post.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const post = {
        title: title,
        content: content
      };
  
      // Get the token (probably from localStorage or cookies)
      const token = getToken();
  
      const response = await axios.post(
        `${config.API_URL}/forums/posts`, 
        post, 
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}` // Add the token to the header
          }
        }
      );
  
      if (response.status === 201) {
        history.push("/txd/forums");
        toast({
          title: "Post created.",
          description: "Your new post has been successfully created.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        console.error("Failed to create post", response);
        toast({
          title: "Error",
          description: "There was an error creating the post. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box 
      display="flex" 
      alignItems="center" 
      justifyContent="center"
      bg={bgColor}
      p={4}
    >
      <Box
        w="100%" 
        bg={boxBgColor} 
        p={{ base: 6, sm: 8 }} 
        borderRadius="md" 
        boxShadow="lg" 
        _hover={{ boxShadow: "xl" }}
      >
        <Heading 
          mb={6} 
          fontSize={{ base: "2xl", sm: "3xl" }} 
          color={headingColor} 
          textAlign="center"
        >
          Create a New Post
        </Heading>
        <Stack spacing={5}>
          <Input
            placeholder="Post Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="lg"
            borderColor={inputBorderColor}
            _hover={{ borderColor: hoverBorderColor }}
            _focus={{ borderColor: focusBorderColor }}
            variant="flushed"
            maxLength={maxTitleLength}
            required
          />
          <Text textAlign="right" fontSize="sm" color="gray.500">
            {title.length}/{maxTitleLength}
          </Text>

          <Textarea
            placeholder="Post Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            size="lg"
            borderColor={inputBorderColor}
            _hover={{ borderColor: hoverBorderColor }}
            _focus={{ borderColor: focusBorderColor }}
            variant="flushed"
            minHeight="150px"
            maxLength={maxContentLength}
            required
          />
          <Text textAlign="right" fontSize="sm" color="gray.500">
            {content.length}/{maxContentLength}
          </Text>

          <Button 
            onClick={handleCreatePost} 
            colorScheme="teal" 
            size="lg" 
            mx="auto"
            _hover={{ bg: buttonHoverColor }}
          >
            Create Post
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default CreatePost;
